import React, { useRef, useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";

/**
 * Ketcher molecule editor dialog.
 * Embeds Ketcher via iframe and auto-loads initial SMILES.
 *
 * Props:
 *   visible: boolean
 *   initialSmiles: string — SMILES to load into Ketcher
 *   onConfirm: (newSmiles: string) => void — called when user confirms edit
 *   onClose: () => void
 */
const KetcherDialog = ({ visible, initialSmiles, onConfirm, onClose }) => {
  const iframeRef = useRef(null);
  const [ketcherReady, setKetcherReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const loadedRef = useRef(""); // stores canonicalized SMILES after loading

  useEffect(() => {
    if (visible) {
      setKetcherReady(false);
      setLoading(false);
    }
  }, [visible]);

  // Poll for Ketcher API readiness, auto-load SMILES when ready
  useEffect(() => {
    if (!visible) return;
    const check = setInterval(async () => {
      try {
        if (iframeRef.current?.contentWindow?.ketcher?.setMolecule) {
          clearInterval(check);
          setKetcherReady(true);
          // Auto-load initial SMILES into Ketcher, then read back canonicalized version
          if (initialSmiles?.trim()) {
            await iframeRef.current.contentWindow.ketcher.setMolecule(initialSmiles.trim());
            // Read back the canonicalized SMILES from Ketcher as the reference
            const canonical = await iframeRef.current.contentWindow.ketcher.getSmiles();
            loadedRef.current = (canonical || "").trim();
          }
        }
      } catch (_) {}
    }, 500);
    setTimeout(() => clearInterval(check), 15000);
    return () => clearInterval(check);
  }, [visible, initialSmiles]);

  const handleConfirm = async () => {
    if (!ketcherReady) return;
    setLoading(true);
    try {
      const newSmiles = await iframeRef.current.contentWindow.ketcher.getSmiles();
      // Pass both the original canonicalized SMILES and the new one
      onConfirm(loadedRef.current, (newSmiles || "").trim());
    } catch (err) {
      console.error("Failed to get SMILES from Ketcher:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      header="Molecule Editor"
      visible={visible}
      style={{ width: "820px", height: "620px" }}
      modal
      draggable={false}
      onHide={onClose}
      footer={
        <div className="flex gap-2 justify-content-end align-items-center w-full">
          <span style={{ fontSize: "0.75rem", color: "#94a3b8" }}>
            {ketcherReady ? "Draw or edit the molecule above" : "Loading editor..."}
          </span>
          <div className="flex gap-2">
            <Button label="Cancel" icon="pi pi-times" className="p-button-text p-button-sm" onClick={onClose} />
            <Button
              label="Confirm"
              icon="pi pi-check"
              className="p-button-sm"
              loading={loading}
              disabled={!ketcherReady}
              style={{ background: "linear-gradient(135deg, #6366f1, #4f46e5)", border: "none", color: "#fff", borderRadius: 6 }}
              onClick={handleConfirm}
            />
          </div>
        </div>
      }
    >
      <div style={{ width: "100%", height: "450px", border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
        <iframe
          ref={iframeRef}
          src="/ketcher/index.html?v=2"
          style={{ width: "100%", height: "100%", border: "none" }}
          title="Ketcher"
        />
      </div>
      {!ketcherReady && (
        <div style={{ textAlign: "center", padding: 8, color: "#94a3b8", fontSize: "0.8rem" }}>
          Loading Ketcher editor...
        </div>
      )}
    </Dialog>
  );
};

export default KetcherDialog;
